import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import GameLogosForm from "../tileDialog/GameLogosForm";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { myExpariencesService } from "../../../services";
import {
  addElement,
  updateElement,
} from "../../../reducers/configurator/configuratorSlice";
import { useDispatch, useSelector } from "react-redux";
import { GAME_CONTEST_DURATION } from "../constants";

const PREFIX = "basketballCustom";

const classes = {
  baseContainer: `${PREFIX}-baseContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
};

const BasketballCustomContainer = styled("div")(({ theme }) => ({
  [`& .${classes.baseContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    padding: "0 1rem",
    marginBottom: "1.5rem",
  },
  [`& .${classes.contentContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    justifyContent: "space-between",
  },
}));

const BasketballCustom = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [gameOptions, setGameOptions] = useState(null);
  const ballsHasBeenFetched = useRef(false);

  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );

  const formatBallObject = (obj) => {
    const tmp = { ...obj };
    tmp.ball_id = tmp._id;
    delete tmp._id;
    delete tmp.category;
    return tmp;
  };

  const handleBallChange = (e) => {
    dispatch(
      updateElement({
        layer: "middle",
        data: {
          ...myTile.middle,
          ...formatBallObject(
            gameOptions.find((opt) => opt.title === e.target.value)
          ),
        },
        tile: { row: currentTile.row, column: currentTile.column },
      })
    );
  };

  const handleAttributeChange = (e, key) => {
    dispatch(
      updateElement({
        layer: "middle",
        data: {
          ...myTile.middle,
          [key]: e.target.value,
        },
        tile: { row: currentTile.row, column: currentTile.column },
      })
    );
  };

  useEffect(() => {
    if (!gameOptions && !ballsHasBeenFetched.current) {
      ballsHasBeenFetched.current = true;
      myExpariencesService
        .fetchGameTypes("balls", "basket")
        .then((res) => {
          const tmp = !myTile.middle.ball_id
            ? formatBallObject(
                res.find((ball) => ball.ball.includes("/ball.glb"))
              )
            : formatBallObject(
                res.find((ball) => myTile.middle.ball_id === ball._id)
              );
          dispatch(
            addElement({
              layer: "middle",
              data: {
                ...myTile.middle,
                ...tmp,
              },
              tile: { row: myTile.row, column: myTile.column },
            })
          );
          setGameOptions(res);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.fetchGameBallsError"), {
              variant: "error",
            });
        });
    }
  }, [dispatch, enqueueSnackbar, gameOptions, myTile, t]);

  return (
    <BasketballCustomContainer>
      {gameOptions ? (
        <div className={classes.baseContainer}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography variant="body1" sx={{ marginBottom: "0.5rem" }}>
              {t("myExpariences.form.ballChoice")}
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: "250px",
                margin: "0 auto",
                marginBottom: "1rem",
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="ball-basket-choice">
                  {t("myExpariences.form.ballTitle")}
                </InputLabel>
                <Select
                  labelId="ball-basket-choice"
                  id="ball-basket-choice"
                  value={myTile.middle?.title || ""}
                  renderValue={(v) => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                        transform: "translateY(5px)",
                      }}
                    >
                      <img
                        src={myTile.middle?.ball_preview}
                        alt="preview ball 1"
                        style={{
                          width: "30px",
                          height: "30px",
                          transform: "translateY(-5px)",
                          marginRight: "0.5rem",
                        }}
                      />
                      <Typography variant="body1">{v}</Typography>
                      <img
                        src={myTile.middle?.ball_bonus_preview}
                        alt="preview ball 1 bonus"
                        style={{
                          width: "30px",
                          height: "30px",
                          transform: "translateY(-5px)",
                          marginLeft: "0.5rem",
                        }}
                      />
                    </Box>
                  )}
                  label={t("myExpariences.form.ballTitle")}
                  onChange={handleBallChange}
                >
                  {gameOptions.map((ball, index) => (
                    <MenuItem
                      value={ball.title}
                      id={ball._id}
                      key={`${ball._id}-${index + 1}`}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <img
                        src={ball.ball_preview}
                        alt="preview ball"
                        style={{
                          width: "30px",
                          height: "30px",
                          marginRight: "0.5rem",
                        }}
                      />
                      {ball.title}
                      <img
                        src={ball.ball_bonus_preview}
                        alt="preview bonus ball"
                        style={{
                          width: "30px",
                          height: "30px",
                          marginLeft: "0.5rem",
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </div>
      ) : (
        <Skeleton height={70} />
      )}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <FormControl
          sx={{ marginBottom: "2rem", width: "100%", maxWidth: "250px" }}
        >
          <InputLabel id="game-contest-duration-choice">
            {t("myExpariences.form.gameContestDurationTitle")}
          </InputLabel>
          <Select
            labelId="game-contest-duration-choice"
            id="game-contest-duration-choice"
            value={myTile.middle?.gameContestDuration || "120"}
            label={t("myExpariences.form.gameContestDurationTitle")}
            onChange={(v) => handleAttributeChange(v, "gameContestDuration")}
          >
            {GAME_CONTEST_DURATION.map((time, index) => (
              <MenuItem value={time} key={`${time}-${index + 1}`}>
                {time}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <div className={classes.baseContainer}>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketGroundLogo")}
          </Typography>
          <GameLogosForm logoKey={"groundLogo"} />
        </div>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.courtLogo")}
          </Typography>
          <GameLogosForm logoKey={"courtLogo"} />
        </div>
      </div>
      <div className={classes.baseContainer}>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketBasketLogo")}
          </Typography>
          <GameLogosForm logoKey={"basketLogo"} />
        </div>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketBallLogo")}
          </Typography>
          <GameLogosForm logoKey={"ballLogo"} />
        </div>
      </div>
    </BasketballCustomContainer>
  );
};

export default BasketballCustom;
